import React, { useState, useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import {
  FaGoogle,
  FaCloud,
  FaUsers,
  FaChartLine,
  FaShieldAlt,
  FaCheckCircle,
  FaHeadset,
  FaBriefcase,
  FaArrowRight,
  FaRegClock,
  FaStar,
} from "react-icons/fa";
import LoadingSpinner from "../components/LoadingSpinner";
import { database } from "../firebase";
import { ref, onValue } from "firebase/database";

// ── 2026 리브랜딩 Google Workspace 로고 (로컬)
import logoGmail      from "../images/google/Google-Workspace-logos/Gmail/logo_gmail_2026q2_color_2x_web_48dp.png";
import logoDrive      from "../images/google/Google-Workspace-logos/Drive/logo_drive_2026q2_color_2x_web_48dp.png";
import logoMeet       from "../images/google/Google-Workspace-logos/Meet/logo_meet_2026q2_color_2x_web_48dp.png";
import logoCalendar   from "../images/google/Google-Workspace-logos/Calendar/logo_calendar_2026q2_color_2x_web_48dp.png";
import logoChat       from "../images/google/Google-Workspace-logos/Chat/logo_chat_2026q2_color_2x_web_48dp.png";
import logoDocs       from "../images/google/Google-Workspace-logos/Docs/logo_docs_2026q2_color_2x_web_48dp.png";
import logoSheets     from "../images/google/Google-Workspace-logos/Sheets/logo_sheets_2026q2_color_2x_web_48dp.png";
import logoSlides     from "../images/google/Google-Workspace-logos/Slides/logo_slides_2026q2_color_2x_web_48dp.png";
import logoForms      from "../images/google/Google-Workspace-logos/Forms/logo_forms_2026q2_color_2x_web_48dp.png";
import logoKeep       from "../images/google/Google-Workspace-logos/Keep/logo_keep_2026q2_color_2x_web_48dp.png";
import logoSites      from "../images/google/Google-Workspace-logos/Sites/logo_sites_2026q2_color_2x_web_48dp.png";
import logoVoice      from "../images/google/Google-Workspace-logos/Voice/logo_voice_2026q2_color_2x_web_48dp.png";
import logoGemini     from "../images/google/Google-Workspace-logos/gemini/gemini-color.svg";
import logoVids       from "../images/google/Google-Workspace-logos/vids/icons8-vids-96.png";
import logoTasks      from "../images/google/Google-Workspace-logos/tasks/icons8-google-tasks-96.png";
import logoNotebookLM from "../images/google/Google-Workspace-logos/notebookLM/notebooklm.svg";
import logoAppSheet   from "../images/google/Google-Workspace-logos/appsheet/icons8-google-appsheet-96.png";

const Popup = lazy(() => import("../components/Popup"));

/* ─── 날짜 포맷 ─────────────────────────────── */
const formatDate = (timestamp) => {
  if (!timestamp) return "";
  try {
    if (typeof timestamp === "string") {
      const parts = timestamp.split(".");
      if (parts.length >= 3)
        return `${parts[0].trim()}. ${parts[1].trim()}. ${parts[2].trim()}`;
    }
    const date = new Date(timestamp);
    if (!isNaN(date.getTime()))
      return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}`;
    return "";
  } catch {
    return "";
  }
};

/* ─── Google Workspace 제품 목록 (2026 리브랜딩 로고 및 한글 설명) ── */
const GWS_PRODUCTS = [
  { name: "Gmail",      icon: logoGmail,      desc: "스마트하고 안전한 기업용 이메일" },
  { name: "Drive",      icon: logoDrive,      desc: "안전한 클라우드 파일 저장 및 공유" },
  { name: "Meet",       icon: logoMeet,       desc: "보안이 강력한 고화질 화상 회의" },
  { name: "Calendar",   icon: logoCalendar,   desc: "팀과 연동되는 스마트 일정 관리" },
  { name: "Chat",       icon: logoChat,       desc: "실시간 메신저 및 협업용 채팅 공간" },
  { name: "Gemini",     icon: logoGemini,     desc: "업무 생산성을 돕는 지능형 AI 비서" },
  { name: "Docs",       icon: logoDocs,       desc: "실시간 협업이 가능한 문서 작성" },
  { name: "Sheets",     icon: logoSheets,     desc: "공동 편집이 편리한 스프레드시트" },
  { name: "Slides",     icon: logoSlides,     desc: "아이디어를 시각화하는 프레젠테이션" },
  { name: "Vids",       icon: logoVids,       desc: "간편한 비즈니스 동영상 제작" },
  { name: "Keep",       icon: logoKeep,       desc: "생각과 아이디어를 기록하는 메모" },
  { name: "Sites",      icon: logoSites,      desc: "쉬운 기업 내부 및 외부 웹사이트 빌더" },
  { name: "Forms",      icon: logoForms,      desc: "설문조사 및 질문지 작성 도구" },
  { name: "Tasks",      icon: logoTasks,      desc: "체계적인 할 일 및 프로젝트 일정 관리" },
  { name: "NotebookLM", icon: logoNotebookLM, desc: "노트 기반 AI 요약 및 연구 플랫폼" },
  { name: "AppSheet",   icon: logoAppSheet,   desc: "코딩 없이 빠르게 만드는 앱 개발" },
  { name: "Voice",      icon: logoVoice,      desc: "클라우드 기반 비즈니스 번호 전화" },
];

/* ─── Google Partner Badge ───────────────────── */
const GooglePartnerBadge = () => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    background: "white", borderRadius: "16px",
    padding: "20px 28px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: "1px solid #e5e7eb",
    transition: "transform 0.3s",
  }}
    onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
    onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
  >
    <img
      src="https://www.gstatic.com/partners/badge/images/2022/PartnerBadgeClickable.svg"
      alt="Google Cloud 공식 파트너"
      width="200" height="70"
    />
    <div style={{ textAlign: "center", marginTop: "10px" }}>
      <div style={{ fontWeight: 700, fontSize: "15px", color: "#1f2937" }}>공식 파트너사</div>
      <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "2px" }}>2024-2025 Verified Partner</div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════
   HOME COMPONENT
   ═══════════════════════════════════════════════ */
function Home() {
  const [noticePosts, setNoticePosts] = useState([]);
  const [updatePosts, setUpdatePosts] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [heroIn, setHeroIn] = useState(false);

  /* 히어로 애니메이션 */
  useEffect(() => { setTimeout(() => setHeroIn(true), 80); }, []);

  /* Firebase 데이터 */
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const noticeRef = ref(database, "posts/notice");
        const updateRef = ref(database, "posts/update");
        const [ns, us] = await Promise.all([
          new Promise(r => onValue(noticeRef, r, { onlyOnce: true })),
          new Promise(r => onValue(updateRef, r, { onlyOnce: true })),
        ]);
        const nd = ns.val(), ud = us.val();
        setNoticePosts(nd ? Object.entries(nd).reverse().map(([id, v]) => ({ id, ...v })).slice(0, 5) : []);
        setUpdatePosts(ud ? Object.entries(ud).reverse().map(([id, v]) => ({ id, ...v })).slice(0, 5) : []);
      } catch (e) { console.error("데이터 로딩 오류:", e); }
    };
    fetchPosts();
  }, []);

  /* 팝업 */
  useEffect(() => {
    const h = localStorage.getItem("hidePopupUntil");
    setIsPopupOpen(!(h && new Date().getTime() < Number(h)));
  }, []);
  const closePopup = () => setIsPopupOpen(false);
  const dontShowForDay = () => {
    localStorage.setItem("hidePopupUntil", new Date().getTime() + 86400000);
    closePopup();
  };

  /* ─── 공통 스타일 ─────── */
  const fadeUp = (delay = 0) => ({
    opacity: heroIn ? 1 : 0,
    transform: heroIn ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.75s ${delay}s ease, transform 0.75s ${delay}s ease`,
  });

  return (
    <div className="home" style={{ fontFamily: "'Noto Sans KR', 'Poppins', sans-serif" }}>

      {/* ═══ 1. 상단 파트너 배너 ═══ */}
      <div style={{
        background: "linear-gradient(135deg,#1a56db 0%,#1e40af 60%,#1d4ed8 100%)",
        position: "relative", overflow: "hidden",
        boxShadow: "0 2px 10px rgba(29,78,216,.35)",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(circle,rgba(255,255,255,.12) 1px,transparent 1px)",
          backgroundSize: "26px 26px",
        }} />
        <div className="container mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center relative z-10 gap-2 md:gap-0">
          <div className="flex items-center gap-4">
            <img src="https://www.gstatic.com/partners/badge/images/2022/PartnerBadgeClickable.svg"
              alt="Google 공식 파트너" className="h-8 hidden md:block" />
            <div>
              <p className="text-white font-semibold text-sm md:text-base flex items-center gap-2">
                <span style={{
                  background: "white", color: "#1a56db",
                  fontSize: "10px", padding: "2px 9px",
                  borderRadius: "999px", fontWeight: 800, letterSpacing: ".06em",
                }}>OFFICIAL</span>
                Google Cloud &amp; Workspace 공식 파트너사
              </p>
              <p className="text-blue-200 text-xs mt-0.5">비즈니스 혁신을 위한 최고의 파트너</p>
            </div>
          </div>
          <div className="flex gap-2">
            <a href="http://pf.kakao.com/_jyebn/chat" target="_blank" rel="noopener noreferrer"
              className="text-xs md:text-sm bg-white text-blue-700 px-3 py-1.5 rounded-lg flex items-center font-semibold hover:bg-blue-50 transition-all">
              <FaGoogle className="mr-1.5" />무료 체험 신청
            </a>
            <a href="http://pf.kakao.com/_jyebn/chat" target="_blank" rel="noopener noreferrer"
              className="text-xs md:text-sm px-3 py-1.5 rounded-lg flex items-center font-semibold transition-all"
              style={{ background: "#FEE500", color: "#000" }}>
              <img src="https://img.icons8.com/color/16/000000/kakaotalk.png" alt="kakao" className="mr-1.5" />
              상담 문의
            </a>
          </div>
        </div>
      </div>

      {/* ═══ 2. 히어로 섹션 ═══ */}
      <section style={{
        background: "linear-gradient(135deg,#0f172a 0%,#1e3a8a 45%,#1d4ed8 100%)",
        minHeight: "500px", position: "relative", overflow: "hidden",
        display: "flex", alignItems: "center",
      }}>
        {/* 장식 원 */}
        <div style={{ position:"absolute", top:"-100px", right:"-100px", width:"450px", height:"450px",
          borderRadius:"50%", background:"rgba(255,255,255,.035)", pointerEvents:"none" }} />
        <div style={{ position:"absolute", bottom:"-80px", left:"-80px", width:"350px", height:"350px",
          borderRadius:"50%", background:"rgba(96,165,250,.08)", pointerEvents:"none" }} />
        <div style={{
          position:"absolute", inset:0,
          backgroundImage:"radial-gradient(circle,rgba(255,255,255,.06) 1.5px,transparent 1.5px)",
          backgroundSize:"34px 34px",
        }} />
        <div className="container mx-auto px-4 text-center relative z-10" style={{ padding:"54px 0" }}>
          <h1 style={{ ...fadeUp(0.25), color:"white", fontSize:"clamp(24px,5vw,46px)", fontWeight:800,
            marginTop:"12px", marginBottom:"18px", lineHeight:1.25 }}>
            비즈니스 생산성을 높이는<br />
            <span style={{
              background:"linear-gradient(to right,#60a5fa,#93c5fd)",
              WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
            }}>Google Workspace</span>
          </h1>

          <p style={{ ...fadeUp(0.35), color:"#93c5fd", fontSize:"clamp(14px,2.5vw,17px)",
            maxWidth:"640px", margin:"0 auto 20px", lineHeight:1.7, fontWeight:500 }}>
            이메일, 드라이브, 화상회의, 문서 협업까지 하나로 완벽하게 해결하세요.<br />
            구글 공식 파트너사가 맞춤형 솔루션을 제안해 드립니다.
          </p>

          <div style={{ ...fadeUp(0.45), display:"flex", gap:"14px", justifyContent:"center", flexWrap:"wrap" }}>
            <a href="http://pf.kakao.com/_jyebn/chat" target="_blank" rel="noopener noreferrer"
              style={{
                display:"inline-flex", alignItems:"center", gap:"10px",
                background:"#FEE500", color:"#000",
                padding:"15px 32px", borderRadius:"12px",
                fontWeight:800, fontSize:"16px", textDecoration:"none",
                boxShadow:"0 4px 20px rgba(254,229,0,.35)",
                transition:"transform .2s",
              }}
              onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"}
              onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}
            >
              <img src="https://img.icons8.com/color/20/000000/kakaotalk.png" alt="kakao" />
              무료 체험 신청하기
            </a>
            <Link to="/productinfo/pricing"
              style={{
                display:"inline-flex", alignItems:"center", gap:"8px",
                background:"rgba(255,255,255,.1)", color:"white",
                border:"1px solid rgba(255,255,255,.3)",
                padding:"15px 28px", borderRadius:"12px",
                fontWeight:600, fontSize:"15px", textDecoration:"none",
                transition:"background .2s",
              }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.2)"}
              onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,.1)"}
            >
              자세히 보기 <FaArrowRight size={13} />
            </Link>
          </div>

          {/* 30일 무료체험 강조 배지 */}
          <div style={{ ...fadeUp(0.45), display:"flex", justifyContent:"center", marginTop:"44px" }}>
            <div style={{
              display:"inline-flex", alignItems:"center", gap:"8px",
              background:"rgba(255,255,255,.08)", border:"1px solid rgba(255,255,255,.15)",
              borderRadius:"999px", padding:"6px 20px",
            }}>
              <span style={{
                background:"#22c55e", color:"white", fontSize:"10px",
                padding:"1px 7px", borderRadius:"999px", fontWeight:800,
              }}>PROMOTION</span>
              <span style={{ color:"white", fontSize:"13px", fontWeight:500 }}>
                공식 파트너 한정 단독 제공: <strong style={{ color:"#fbbf24" }}>30일 무료체험</strong> 혜택
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 3. GWS 제품 아이콘 띠 (마우스 오버 툴팁 포함) ═══ */}
      <section style={{ background:"#f8faff", padding:"44px 0", borderBottom:"1px solid #e5e7eb" }}>
        <div className="container mx-auto px-4">
          <p style={{
            textAlign:"center", color:"#9ca3af", fontSize:"12px",
            fontWeight:700, letterSpacing:".12em", textTransform:"uppercase", marginBottom:"22px",
          }}>
            Google Workspace에 포함된 제품
          </p>
          <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"center", gap:"18px 24px" }}>
            {GWS_PRODUCTS.map(p => (
              <div key={p.name}
                className="gws-product-item"
                style={{
                  display:"flex",
                  flexDirection:"column",
                  alignItems:"center",
                  gap:"6px",
                  cursor:"pointer",
                  position:"relative",
                  transition:"transform .2s"
                }}
                onMouseEnter={e=>e.currentTarget.style.transform="translateY(-5px)"}
                onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}
              >
                <img src={p.icon} alt={p.name} width="40" height="40" loading="lazy"
                  onError={e=>e.target.style.display="none"} />
                <span style={{ fontSize:"11px", color:"#6b7280", fontWeight:500 }}>{p.name}</span>
                
                {/* 툴팁 마크업 */}
                <div className="gws-tooltip">
                  <strong>{p.name}</strong>
                  <p>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 툴팁 CSS 스타일 */}
        <style>{`
          .gws-product-item .gws-tooltip {
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%) translateY(-8px);
            background: rgba(15, 23, 42, 0.95);
            color: #ffffff;
            padding: 8px 12px;
            border-radius: 8px;
            font-size: 11px;
            white-space: nowrap;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
            transition: opacity 0.2s, transform 0.2s;
            z-index: 100;
            text-align: center;
          }
          .gws-product-item .gws-tooltip::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-width: 5px;
            border-style: solid;
            border-color: rgba(15, 23, 42, 0.95) transparent transparent transparent;
          }
          .gws-product-item:hover .gws-tooltip {
            opacity: 1;
            visibility: visible;
            transform: translateX(-50%) translateY(-12px);
          }
          .gws-tooltip strong {
            display: block;
            color: #60a5fa;
            font-size: 12px;
            margin-bottom: 2px;
          }
          .gws-tooltip p {
            margin: 0;
            color: #e2e8f0;
          }
        `}</style>
      </section>

      {/* ═══ 4. 파트너 배지 섹션 ═══ */}
      <section className="container mx-auto px-4 my-12 md:my-16">
        <div style={{
          background:"linear-gradient(135deg,#eff6ff 0%,#dbeafe 100%)",
          borderRadius:"22px", padding:"clamp(24px,4vw,52px)",
          border:"1px solid #bfdbfe", position:"relative", overflow:"hidden",
        }}>
          {/* 격자 패턴 */}
          <div style={{
            position:"absolute", inset:0, opacity:.1,
            backgroundImage:"linear-gradient(#3b82f6 1px,transparent 1px),linear-gradient(90deg,#3b82f6 1px,transparent 1px)",
            backgroundSize:"28px 28px",
          }} />
          <div className="flex flex-col md:flex-row items-center justify-center gap-10 md:gap-16 relative z-10">
            <div className="flex flex-col items-center gap-3">
              <GooglePartnerBadge />
              <span style={{
                background:"#dbeafe", color:"#1e40af",
                fontSize:"11px", fontWeight:700,
                padding:"4px 12px", borderRadius:"999px",
                border:"1px solid #bfdbfe",
              }}>2024-2025 인증 파트너</span>
            </div>
            <div style={{ maxWidth:"440px" }}>
              <h2 style={{ color:"#1e3a8a", fontSize:"clamp(18px,3vw,26px)", fontWeight:800, marginBottom:"12px" }}>
                구글 공식 파트너사와 함께하세요
              </h2>
              <p style={{ color:"#374151", fontSize:"14px", lineHeight:1.85, marginBottom:"18px" }}>
                구글로부터 직접 인증받은 파트너사로서{" "}
                <strong>전문적인 서비스</strong>와 <strong>특별한 혜택</strong>을 제공합니다.
                검증된 전문성과 엔터프라이즈급 지원으로 비즈니스 성장을 가속화하세요.
              </p>
              <ul style={{ listStyle:"none", padding:0, margin:"0 0 20px" }}>
                {[
                  "비즈니스 맞춤형 도입 컨설팅 및 교육 무료 제공",
                  "일반 고객 대비 확장된 30일 무료 체험 기간",
                  "라이선스 구매 시 특별 할인 및 프로모션 제공",
                ].map((t,i) => (
                  <li key={i} style={{ display:"flex", alignItems:"flex-start", gap:"8px",
                    marginBottom:"9px", fontSize:"13px", color:"#374151" }}>
                    <FaCheckCircle style={{ color:"#1d4ed8", flexShrink:0, marginTop:"2px" }} />
                    {t}
                  </li>
                ))}
              </ul>
              <div style={{ display:"flex", flexWrap:"wrap", gap:"6px", marginBottom:"22px" }}>
                {[
                  ["검증된 전문성","#dbeafe","#1e40af"],
                  ["특별 할인","#dcfce7","#166534"],
                  ["맞춤형 서비스","#fef9c3","#854d0e"],
                  ["기술 지원","#fee2e2","#991b1b"],
                  ["무료 교육","#f3e8ff","#6b21a8"],
                ].map(([txt,bg,col]) => (
                  <span key={txt} style={{ background:bg, color:col,
                    fontSize:"11px", padding:"4px 11px",
                    borderRadius:"999px", fontWeight:600 }}>{txt}</span>
                ))}
              </div>
              <a href="http://pf.kakao.com/_jyebn/chat" target="_blank" rel="noopener noreferrer"
                style={{
                  display:"inline-flex", alignItems:"center", gap:"8px",
                  background:"#1d4ed8", color:"white",
                  padding:"11px 22px", borderRadius:"10px",
                  fontWeight:700, fontSize:"14px", textDecoration:"none",
                  boxShadow:"0 4px 14px rgba(29,78,216,.3)",
                  transition:"background .2s,transform .2s",
                }}
                onMouseEnter={e=>{e.currentTarget.style.background="#1e40af";e.currentTarget.style.transform="translateY(-1px)";}}
                onMouseLeave={e=>{e.currentTarget.style.background="#1d4ed8";e.currentTarget.style.transform="translateY(0)";}}
              >
                파트너십 혜택 상담받기 <FaArrowRight size={13} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <main>
        {/* ═══ 5. 핵심 서비스 카드 ═══ */}
        <section className="container mx-auto px-4 mb-20">
          <div style={{ textAlign:"center", marginBottom:"48px" }}>
            <span style={{
              background:"#eff6ff", color:"#1d4ed8",
              fontSize:"12px", fontWeight:700,
              padding:"6px 16px", borderRadius:"999px",
              letterSpacing:".05em", textTransform:"uppercase",
              display:"inline-block", marginBottom:"12px"
            }}>Core Services</span>
            <h2 style={{ fontSize:"clamp(22px,3.5vw,32px)", fontWeight:800, color:"#0f172a", lineHeight:1.3 }}>
              Google Workspace 핵심 서비스 솔루션
            </h2>
            <div style={{ width:"40px", height:"3px", background:"#1d4ed8", margin:"16px auto 0", borderRadius:"2px" }} />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon:<FaGoogle />, accent:"#4285F4", bg:"rgba(66, 133, 244, 0.08)", bdr:"rgba(66, 133, 244, 0.15)",
                title:"스마트 비즈니스 오피스",
                desc:"맞춤 도메인 메일(Gmail), 안전한 파일 공유(Drive), HD 화상회의(Meet) 등 현대 비즈니스에 필요한 핵심 요소를 하나의 통합 솔루션으로 제공하여 업무 생산성을 극대화합니다.",
                tag:"이메일 & 회의 & 클라우드",
                link:"/serviceintro/overview" },
              { icon:<FaCloud />, accent:"#EA4335", bg:"rgba(234, 67, 53, 0.08)", bdr:"rgba(234, 67, 53, 0.15)",
                title:"엔터프라이즈급 스토리지",
                desc:"언제 어디서나 접근 및 편집이 가능한 공유 드라이브와 정교한 액세스 제어로 기업의 중요 정보 자산을 완벽하게 제어하고 안전하게 팀 간에 동기화할 수 있도록 지원합니다.",
                tag:"무제한 공간 & 권한 제어",
                link:"/serviceintro/features" },
              { icon:<FaUsers />, accent:"#34A853", bg:"rgba(52, 168, 83, 0.08)", bdr:"rgba(52, 168, 83, 0.15)",
                title:"실시간 공동 협업 엔진",
                desc:"설치 없이 브라우저에서 즉시 Docs, Sheets, Slides를 활용해 동료들과 실시간 동시 협업을 진행해보세요. 모든 변경사항은 자동으로 저장되고 스마트하게 복원됩니다.",
                tag:"동시 편집 & 자동 저장",
                link:"/serviceintro/benefits" },
            ].map((c, i) => (
              <div key={i}
                className="gws-service-card"
                style={{
                  background:"#ffffff",
                  borderRadius:"24px",
                  padding:"40px 32px",
                  boxShadow:"0 10px 30px rgba(15, 23, 42, 0.04)",
                  border:"1px solid #f1f5f9",
                  position:"relative",
                  overflow:"hidden",
                  display:"flex",
                  flexDirection:"column",
                  transition:"all 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
                }}
                onMouseEnter={e=>{
                  e.currentTarget.style.transform="translateY(-8px)";
                  e.currentTarget.style.boxShadow="0 20px 40px rgba(15, 23, 42, 0.08)";
                  e.currentTarget.style.borderColor=c.accent;
                }}
                onMouseLeave={e=>{
                  e.currentTarget.style.transform="translateY(0)";
                  e.currentTarget.style.boxShadow="0 10px 30px rgba(15, 23, 42, 0.04)";
                  e.currentTarget.style.borderColor="#f1f5f9";
                }}
              >
                {/* 상단 액센트 탑바 */}
                <div style={{ position:"absolute", top:0, left:0, right:0, height:"4px", background:c.accent }} />
                
                {/* 아이콘 */}
                <div style={{
                  width:"60px",
                  height:"60px",
                  borderRadius:"16px",
                  background:c.bg,
                  border:`1px solid ${c.bdr}`,
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center",
                  color:c.accent,
                  fontSize:"24px",
                  marginBottom:"28px",
                  transition:"transform 0.3s",
                }}>{c.icon}</div>
                
                {/* 태그 */}
                <span style={{
                  fontSize:"12px",
                  fontWeight:600,
                  color:c.accent,
                  background:c.bg,
                  padding:"4px 12px",
                  borderRadius:"999px",
                  alignSelf:"flex-start",
                  marginBottom:"14px",
                }}>{c.tag}</span>

                {/* 타이틀 */}
                <h3 style={{ fontWeight:800, fontSize:"20px", color:"#0f172a", marginBottom:"14px", letterSpacing:"-0.02em" }}>
                  {c.title}
                </h3>
                
                {/* 설명 */}
                <p style={{
                  color:"#64748b",
                  fontSize:"14px",
                  lineHeight:1.75,
                  marginBottom:"30px",
                  flexGrow:1,
                }}>{c.desc}</p>
                
                {/* 하단 링크 */}
                <Link to={c.link} style={{
                  color:c.accent,
                  fontWeight:700,
                  fontSize:"14px",
                  display:"inline-flex",
                  alignItems:"center",
                  gap:"6px",
                  textDecoration:"none",
                  transition:"gap 0.2s",
                }}
                  onMouseEnter={e=>e.currentTarget.style.gap="10px"}
                  onMouseLeave={e=>e.currentTarget.style.gap="6px"}
                >
                  상세 솔루션 보기 <FaArrowRight size={13} />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* ═══ 6. 파트너 혜택 (다크 블루) ═══ */}
        <section style={{
          background:"linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 55%,#2563eb 100%)",
          padding:"68px 0", position:"relative", overflow:"hidden",
        }}>
          <div style={{
            position:"absolute", inset:0, opacity:.07,
            backgroundImage:"linear-gradient(white 1px,transparent 1px),linear-gradient(90deg,white 1px,transparent 1px)",
            backgroundSize:"22px 22px",
          }} />
          <div className="container mx-auto px-4 text-center relative z-10">
            <h2 style={{ color:"white", fontSize:"clamp(20px,3vw,30px)", fontWeight:800, marginBottom:"8px" }}>
              구글 공식 파트너만의 특별한 혜택
            </h2>
            <p style={{ color:"#93c5fd", marginBottom:"44px", fontSize:"15px" }}>
              워크웨이를 통해 더 많은 혜택을 누리세요
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon:<FaCheckCircle size={28} />, title:"공식 파트너 할인",
                  desc:"구글 공식 파트너사만이 제공할 수 있는 특별 할인 혜택을 받아보세요." },
                { icon:<FaHeadset size={28} />, title:"전문가 기술 지원",
                  desc:"구글 인증 전문가의 기술 지원으로 도입부터 활용까지 도와드립니다." },
                { icon:<FaBriefcase size={28} />, title:"맞춤형 컨설팅",
                  desc:"기업 환경에 맞는 맞춤형 도입 컨설팅으로 최적의 솔루션을 제안합니다." },
              ].map((item, i) => (
                <div key={i}
                  style={{
                    background:"rgba(255,255,255,.96)", borderRadius:"16px",
                    padding:"28px 24px", textAlign:"left",
                    boxShadow:"0 8px 28px rgba(0,0,0,.18)",
                    transition:"transform .22s",
                  }}
                  onMouseEnter={e=>e.currentTarget.style.transform="translateY(-5px)"}
                  onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}
                >
                  <div style={{ color:"#1d4ed8", marginBottom:"16px" }}>{item.icon}</div>
                  <h3 style={{ fontWeight:700, fontSize:"17px", color:"#111827", marginBottom:"8px" }}>{item.title}</h3>
                  <p style={{ fontSize:"14px", color:"#6b7280", lineHeight:1.75 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ 7. 비즈니스 성과 지표 ═══ */}
        <section style={{ background:"white", padding:"68px 0" }}>
          <div className="container mx-auto px-4">
            <h2 style={{ textAlign:"center", fontSize:"clamp(20px,3vw,28px)", fontWeight:800,
              color:"#111827", marginBottom:"8px" }}>
              비즈니스 혁신을 경험하세요
            </h2>
            <p style={{ textAlign:"center", color:"#6b7280", marginBottom:"44px", fontSize:"15px" }}>
              구글 클라우드와 워크스페이스로 비즈니스 성과를 극대화하세요
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { icon:<FaChartLine />, title:"생산성 향상",   desc:"실시간 협업으로 40% 향상",   c:"#1d4ed8", bg:"#eff6ff" },
                { icon:<FaCloud />,     title:"클라우드 통합", desc:"모든 데이터를 한 곳에서",    c:"#dc2626", bg:"#fff1f2" },
                { icon:<FaShieldAlt />, title:"기업급 보안",   desc:"엔터프라이즈급 보안 제공",  c:"#059669", bg:"#f0fdf4" },
                { icon:<FaUsers />,     title:"팀워크 강화",   desc:"효율적인 팀 커뮤니케이션",  c:"#d97706", bg:"#fffbeb" },
              ].map((item, i) => (
                <div key={i}
                  style={{
                    textAlign:"center", padding:"28px 16px", borderRadius:"18px",
                    background:"white", border:"1px solid #f3f4f6",
                    boxShadow:"0 2px 14px rgba(0,0,0,.05)",
                    transition:"transform .22s,box-shadow .22s",
                  }}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-5px)";e.currentTarget.style.boxShadow="0 10px 28px rgba(0,0,0,.1)";}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 2px 14px rgba(0,0,0,.05)";}}
                >
                  <div style={{
                    width:"56px", height:"56px", borderRadius:"16px",
                    background:item.bg, color:item.c,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:"24px", margin:"0 auto 16px",
                  }}>{item.icon}</div>
                  <h3 style={{ fontWeight:700, fontSize:"16px", color:"#111827", marginBottom:"6px" }}>{item.title}</h3>
                  <p style={{ fontSize:"13px", color:"#6b7280" }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ 8. 게시판 섹션 ═══ */}
        <section style={{ background:"#f8faff", padding:"52px 0 68px" }}>
          <div className="container mx-auto px-4">
            <h2 style={{ textAlign:"center", fontSize:"clamp(18px,2.5vw,26px)", fontWeight:800,
              color:"#111827", marginBottom:"36px" }}>
              최신 소식
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 공지사항 */}
              <div style={{ background:"white", borderRadius:"18px", overflow:"hidden",
                boxShadow:"0 4px 22px rgba(0,0,0,.07)", border:"1px solid #e5e7eb" }}>
                <div style={{
                  background:"linear-gradient(90deg,#1d4ed8,#3b82f6)",
                  padding:"16px 22px",
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                }}>
                  <h3 style={{ color:"white", fontWeight:700, fontSize:"16px", margin:0,
                    display:"flex", alignItems:"center", gap:"8px" }}>
                    <FaRegClock size={14} /> 공지사항
                  </h3>
                  <Link to="/board/notice" style={{
                    color:"rgba(255,255,255,.85)", fontSize:"13px", textDecoration:"none",
                    display:"flex", alignItems:"center", gap:"4px",
                  }}>더보기 <FaArrowRight size={11} /></Link>
                </div>
                <ul style={{ listStyle:"none", padding:"6px 22px 16px", margin:0 }}>
                  {noticePosts.length > 0 ? noticePosts.map(post => (
                    <li key={post.id} style={{
                      display:"flex", justifyContent:"space-between", alignItems:"center",
                      padding:"11px 0", borderBottom:"1px solid #f3f4f6",
                    }}>
                      <Link to={`/board/notice/${post.id}`} style={{
                        color:"#374151", fontSize:"14px", textDecoration:"none",
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                        maxWidth:"68%", transition:"color .15s",
                      }}
                        onMouseEnter={e=>e.currentTarget.style.color="#1d4ed8"}
                        onMouseLeave={e=>e.currentTarget.style.color="#374151"}
                      >{post.title}</Link>
                      <span style={{ color:"#9ca3af", fontSize:"12px", flexShrink:0 }}>
                        {formatDate(post.createdAt)}
                      </span>
                    </li>
                  )) : (
                    <li style={{ padding:"22px 0", textAlign:"center", color:"#9ca3af", fontSize:"14px" }}>
                      등록된 공지사항이 없습니다.
                    </li>
                  )}
                </ul>
              </div>

              {/* 구글 업데이트 */}
              <div style={{ background:"white", borderRadius:"18px", overflow:"hidden",
                boxShadow:"0 4px 22px rgba(0,0,0,.07)", border:"1px solid #e5e7eb" }}>
                <div style={{
                  background:"linear-gradient(90deg,#059669,#34d399)",
                  padding:"16px 22px",
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                }}>
                  <h3 style={{ color:"white", fontWeight:700, fontSize:"16px", margin:0,
                    display:"flex", alignItems:"center", gap:"8px" }}>
                    <FaStar size={14} /> 구글 업데이트
                  </h3>
                  <Link to="/board/update" style={{
                    color:"rgba(255,255,255,.85)", fontSize:"13px", textDecoration:"none",
                    display:"flex", alignItems:"center", gap:"4px",
                  }}>더보기 <FaArrowRight size={11} /></Link>
                </div>
                <ul style={{ listStyle:"none", padding:"6px 22px 16px", margin:0 }}>
                  {updatePosts.length > 0 ? updatePosts.map(post => (
                    <li key={post.id} style={{
                      display:"flex", justifyContent:"space-between", alignItems:"center",
                      padding:"11px 0", borderBottom:"1px solid #f3f4f6",
                    }}>
                      <Link to={`/board/update/${post.id}`} style={{
                        color:"#374151", fontSize:"14px", textDecoration:"none",
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                        maxWidth:"68%", transition:"color .15s",
                      }}
                        onMouseEnter={e=>e.currentTarget.style.color="#059669"}
                        onMouseLeave={e=>e.currentTarget.style.color="#374151"}
                      >{post.title}</Link>
                      <span style={{ color:"#9ca3af", fontSize:"12px", flexShrink:0 }}>
                        {formatDate(post.createdAt)}
                      </span>
                    </li>
                  )) : (
                    <li style={{ padding:"22px 0", textAlign:"center", color:"#9ca3af", fontSize:"14px" }}>
                      등록된 업데이트가 없습니다.
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ 9. 하단 CTA ═══ */}
        <section style={{
          background:"linear-gradient(135deg,#0f172a 0%,#1e3a8a 50%,#1d4ed8 100%)",
          padding:"72px 0", position:"relative", overflow:"hidden",
        }}>
          {/* 빛나는 글로우 */}
          <div style={{
            position:"absolute", top:"50%", left:"50%",
            transform:"translate(-50%,-50%)",
            width:"700px", height:"700px", borderRadius:"50%",
            background:"radial-gradient(circle,rgba(96,165,250,.13) 0%,transparent 70%)",
            pointerEvents:"none",
          }} />
          <div style={{
            position:"absolute", inset:0,
            backgroundImage:"radial-gradient(circle,rgba(255,255,255,.07) 1.5px,transparent 1.5px)",
            backgroundSize:"30px 30px",
          }} />

          <div className="container mx-auto px-4 text-center relative z-10">
            <div style={{
              display:"inline-flex", alignItems:"center", gap:"8px",
              background:"rgba(255,255,255,.1)", border:"1px solid rgba(255,255,255,.2)",
              borderRadius:"999px", padding:"6px 18px", marginBottom:"22px",
            }}>
              <span style={{ color:"#fbbf24", fontSize:"13px", fontWeight:700 }}>🎁 한정 혜택</span>
            </div>
            <h2 style={{ color:"white", fontSize:"clamp(22px,4vw,38px)", fontWeight:800,
              marginBottom:"14px", lineHeight:1.3 }}>
              구글 공식 파트너와<br />지금 바로 시작하세요
            </h2>
            <p style={{ color:"#93c5fd", fontSize:"16px", marginBottom:"10px" }}>
              <strong style={{ color:"#fbbf24" }}>30일 무료 체험</strong>으로 Google Workspace를 경험해보세요
            </p>
            <div style={{ marginBottom:"38px" }}>
              <span style={{
                background:"#fbbf24", color:"#78350f",
                fontSize:"13px", padding:"5px 16px", borderRadius:"999px",
                fontWeight:700, display:"inline-block",
              }}>🔥 구글 공식 파트너사 단독 혜택: 14일 → 30일 확장</span>
            </div>
            <div style={{ display:"flex", gap:"12px", justifyContent:"center", flexWrap:"wrap" }}>
              <a href="http://pf.kakao.com/_jyebn/chat" target="_blank" rel="noopener noreferrer"
                style={{
                  display:"inline-flex", alignItems:"center", gap:"10px",
                  background:"#FEE500", color:"#000",
                  padding:"17px 38px", borderRadius:"14px",
                  fontWeight:800, fontSize:"17px", textDecoration:"none",
                  boxShadow:"0 6px 28px rgba(254,229,0,.48)",
                  transition:"transform .2s,box-shadow .2s",
                  animation:"ctaPulse 2.8s infinite",
                }}
                onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px) scale(1.02)";e.currentTarget.style.boxShadow="0 12px 36px rgba(254,229,0,.62)";}}
                onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0) scale(1)";e.currentTarget.style.boxShadow="0 6px 28px rgba(254,229,0,.48)";}}
              >
                <img src="https://img.icons8.com/color/24/000000/kakaotalk.png" alt="kakao" />
                무료 체험 및 상담하기
              </a>
              <Link to="/productinfo/pricing"
                style={{
                  display:"inline-flex", alignItems:"center", gap:"8px",
                  background:"rgba(255,255,255,.13)", color:"white",
                  border:"1.5px solid rgba(255,255,255,.3)",
                  padding:"17px 30px", borderRadius:"14px",
                  fontWeight:600, fontSize:"15px", textDecoration:"none",
                  transition:"background .2s",
                }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.22)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,.13)"}
              >
                요금제 보기 <FaArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ═══ 팝업 ═══ */}
      <Suspense fallback={<LoadingSpinner />}>
        <Popup isOpen={isPopupOpen} onClose={closePopup}>
          <div className="popup-content max-w-md mx-auto p-3">
            <div className="flex justify-center mb-3">
              <GooglePartnerBadge />
            </div>
            <h2 className="text-xl font-bold mb-3 text-center text-blue-700">
              구글 공식 파트너사 특별 혜택
            </h2>
            <p className="text-center leading-relaxed text-gray-700 text-sm">
              <span className="bg-yellow-100 px-1 py-0.5 rounded">지금 문의하시면</span>{" "}
              공식 파트너사만의 특별한 혜택을 받을 수 있습니다!<br /><br />
              <span className="text-green-600 font-bold">
                파트너사 혜택: 특별 할인 + 30일 무료 체험 + 맞춤형 도입 컨설팅
              </span><br /><br />
              <span className="italic text-xs text-gray-500">
                * 구글 공식 홈페이지 직접 구매 시 무료 체험은 14일만 가능합니다
              </span>
            </p>
            <div className="mt-4 flex justify-center gap-2">
              <a href="http://pf.kakao.com/_jyebn/chat" target="_blank" rel="noopener noreferrer"
                className="px-4 py-1.5 bg-yellow-400 text-black rounded text-sm font-medium hover:bg-yellow-500 transition flex items-center">
                <img src="https://img.icons8.com/color/20/000000/kakaotalk.png" alt="kakao" className="inline mr-1" />
                혜택 문의하기
              </a>
              <button onClick={dontShowForDay} className="px-3 py-1 bg-gray-600 text-white rounded text-xs">
                오늘 그만보기
              </button>
              <button onClick={closePopup} className="px-3 py-1 bg-blue-600 text-white rounded text-xs">
                닫기
              </button>
            </div>
          </div>
        </Popup>
      </Suspense>




      {/* 글로벌 키프레임 */}
      <style>{`
        @keyframes ctaPulse {
          0%,100% { box-shadow:0 6px 28px rgba(254,229,0,.48); }
          50%      { box-shadow:0 6px 28px rgba(254,229,0,.48),0 0 0 9px rgba(254,229,0,.13); }
        }
      `}</style>
    </div>
  );
}

export default Home;
